<template>
  <div
    class="ns-footer-nav"
    :class="{ rtl: dir === 'rtl', 'ns-minimal-footer-nav': minimal }"
  >
    <!-- ------ -->
    <!-- MOBILE -->
    <!-- ------ -->
    <div class="ns-footer-nav-mobile">
      <div
        v-for="navItem in localNavigation"
        :key="navItem.title"
        class="nav-item--mobile"
        :class="{
          'nav-item--mobile--active': openNavItemTitle === navItem.title
        }"
      >
        <!-- TITLE -->
        <div class="nav-item-panel" @click="handleNavItemClick(navItem.title)">
          <div class="nav-item-title">{{ navItem.title }}</div>
          <NsIcon
            class="nav-item-icon"
            :class="{
              'nav-item-icon--active': openNavItemTitle === navItem.title
            }"
            icon-name="icon-narrow-angle-bottom-24"
            size="custom"
          />
        </div>

        <!-- SUBLINKS -->
        <NsAnimateHeight :open="openNavItemTitle === navItem.title">
          <div class="nav-item-links">
            <div
              v-for="linkItem in navItem.links"
              :key="linkItem.name"
              class="nav-item-link"
            >
              <a
                :href="getFullyQualifiedUrl(linkItem.href)"
                class="nav-item-link-anchor"
                v-html="linkItem.name"
              />
            </div>
          </div>
        </NsAnimateHeight>
      </div>
    </div>

    <!-- ------- -->
    <!-- DESKTOP -->
    <!-- ------- -->
    <div class="ns-footer-nav-desktop">
      <div
        v-for="navItem in localNavigation"
        :key="navItem.title"
        class="nav-item"
      >
        <!-- TITLE -->
        <div class="nav-item-title">{{ navItem.title }}</div>

        <!-- SUBLINKS -->
        <div class="nav-item-links">
          <div
            v-for="linkItem in navItem.links"
            :key="linkItem.name"
            class="nav-item-link"
          >
            <a
              :href="getFullyQualifiedUrl(linkItem.href)"
              class="nav-item-link-anchor"
              v-html="linkItem.name"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { DirUtil } from "@nuskin/ns-util";
import { NsIcon, NsAnimateHeight } from "@nuskin/design-components";
import { fromJsonString, getFullUrl } from "@nuskin/ns-common-lib";

export default {
  name: "NsFooterNav",
  components: { NsIcon, NsAnimateHeight },
  props: {
    /**
     * Display in minimal mode
     */
    minimal: {
      type: Boolean,
      default: false
    },
    /**
     * An array of navigation objects. Each object should contain: title and
     * links with name and href ([{ title, links: [{ name, href }] }])
     */
    navigation: {
      type: [Array, String],
      default() {
        return [];
      },
      validator: value => {
        const nav = fromJsonString(value) || [];
        return (
          Array.isArray(nav) &&
          nav.every(
            item =>
              // has valid title
              item.title &&
              typeof item.title === "string" &&
              // has valid links array
              Array.isArray(item.links) &&
              item.links.every(item =>
                ["name", "href"].every(
                  prop => item[prop] && typeof item[prop] === "string"
                )
              )
          )
        );
      }
    }
  },
  data() {
    return {
      openNavItemTitle: ""
    };
  },
  computed: {
    siteUrl() {
      return this.$NsAppService.siteUrl || "";
    },
    localNavigation() {
      return fromJsonString(this.navigation) || [];
    },
    dir() {
      return DirUtil.getDir();
    }
  },
  methods: {
    handleNavItemClick(title) {
      this.openNavItemTitle = this.openNavItemTitle === title ? "" : title;
    },
    getFullyQualifiedUrl(url) {
      return getFullUrl(this.siteUrl, url);
    }
  }
};
</script>

<style lang="scss" scoped>
@use "sass:map";
@use "~@nuskin/ns-core-styles/src/scss/global.mixins";
@use "~@nuskin/ns-core-styles/src/scss/typographyVariables";
@use "~@nuskin/ns-core-styles/src/scss/other";

.ns-footer-nav {
  color: white;
}

.nav-item--mobile {
  border-top: map.get(other.$otherUtils, global-border);

  &:last-child {
    border-bottom: map.get(other.$otherUtils, global-border);
  }
}

.nav-item--mobile--active {
  border-top-width: 2px;
  border-bottom: map.get(other.$otherUtils, global-border);

  &:last-child {
    border-bottom-width: 2px;
  }
}

.nav-item-panel {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin: 10px 0;
  padding: 0 15px;
  cursor: pointer;
}

.nav-item-icon {
  width: 15px;
  height: 15px;
  transition: map.get(other.$otherUtils, global-transition);

  &.nav-item-icon--active {
    transform: scaleY(-1);
    // transform: rotate(180deg);
  }
}
.nav-item-links {
  padding: 0 15px;
}

.nav-item-link {
  margin-bottom: 10px;
}

.nav-item-title {
  position: relative;
  font-size: 16px;
  font-weight: map.get(typographyVariables.$typography, font-weight-semibold);
  text-transform: uppercase;
}

.nav-item-link-anchor {
  @include global.a-base();
  position: relative;
  display: inline;
  border-bottom: 1px solid transparent;
  color: white;
  background-color: transparent;
  text-align: left;
  font-family: map.get(typographyVariables.$typography, font-family-primary);
  font-weight: map.get(typographyVariables.$typography, font-weight-regular);
  text-decoration: none;
  transition: map.get(other.$otherUtils, global-transition);

  &:hover {
    border-bottom: 1px solid currentColor;
  }
}

.ns-footer-nav-desktop {
  position: relative;
  display: none;
}

.ns-footer-nav.rtl .nav-item-link {
  text-align: right;
}

@media (min-width: 1024px) {
  .ns-footer-nav-mobile {
    display: none;
  }

  .ns-footer-nav-desktop {
    display: flex;
  }

  .nav-item {
    position: relative;
    flex: 1;
    padding: 0 10px;
  }

  .nav-item:first-child {
    padding-left: 0;
  }

  .nav-item:last-child {
    padding-right: 0;
  }

  .nav-item-title {
    margin-bottom: 10px;
    font-size: 13px;
    line-height: 18px;
    touch-action: manipulation;
  }

  .nav-item-links {
    padding: 0;
  }

  .ns-minimal-footer-nav {
    color: black;

    .nav-item-link-anchor {
      color: black;
    }
  }
}

.ns-minimal-footer-nav {
  .nav-item--mobile {
    border-top: 1px solid black;
  }

  .nav-item-link-anchor {
    color: black;
  }

  .nav-item--mobile:last-child {
    border-bottom: 1px solid black;
  }

  .nav-item--mobile--active {
    border-bottom: none;
  }
}
</style>
